import { axiosPrivate } from "../../../api/axios";

const getBasePath = (groupCode, projectCode) =>
  `/oss/${encodeURIComponent(groupCode)}/${encodeURIComponent(projectCode)}`;

export const fetchOssDashboard = async (groupCode, projectCode) => {
  const response = await axiosPrivate.get(
    `${getBasePath(groupCode, projectCode)}/dashboard`
  );
  return response.data?.data;
};

export const fetchOssOrders = async (groupCode, projectCode, params = {}) => {
  const response = await axiosPrivate.get(
    `${getBasePath(groupCode, projectCode)}/orders`,
    { params }
  );
  return response.data?.data;
};

export const fetchOssOrderKpiSummary = async (groupCode, projectCode) => {
  const countOnly = { page: 1, pageSize: 1, groupBy: "order" };
  const [
    all,
    checkRequired,
    changeReview,
    highRisk,
    overdueRisk,
  ] = await Promise.all([
    fetchOssOrders(groupCode, projectCode, countOnly),
    fetchOssOrders(groupCode, projectCode, { ...countOnly, status: "CHECK_REQUIRED" }),
    fetchOssOrders(groupCode, projectCode, { ...countOnly, status: "CHANGE_REVIEW" }),
    fetchOssOrders(groupCode, projectCode, { ...countOnly, riskLevel: "HIGH" }),
    fetchOssOrders(groupCode, projectCode, { ...countOnly, riskLevel: "OVERDUE" }),
  ]);

  return {
    total: Number(all?.pagination?.total || 0),
    checkRequired: Number(checkRequired?.pagination?.total || 0),
    changeReview: Number(changeReview?.pagination?.total || 0),
    deliveryRisk: Number(highRisk?.pagination?.total || 0) + Number(overdueRisk?.pagination?.total || 0),
  };
};

export const createOssOrder = async (groupCode, projectCode, payload) => {
  const response = await axiosPrivate.post(
    `${getBasePath(groupCode, projectCode)}/orders`,
    payload
  );
  return response.data?.data;
};

export const importOssOrdersExcel = async (groupCode, projectCode, file, payload = {}) => {
  const formData = new FormData();
  formData.append("excel", file);
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null) formData.append(key, value);
  });

  const response = await axiosPrivate.post(
    `${getBasePath(groupCode, projectCode)}/orders/import-excel`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data?.data;
};

export const updateOssOrder = async (groupCode, projectCode, orderId, payload) => {
  const response = await axiosPrivate.patch(
    `${getBasePath(groupCode, projectCode)}/orders/${encodeURIComponent(orderId)}`,
    payload
  );
  return response.data?.data;
};

export const cancelOssOrder = async (groupCode, projectCode, orderId, payload) => {
  const response = await axiosPrivate.post(
    `${getBasePath(groupCode, projectCode)}/orders/${encodeURIComponent(orderId)}/cancel`,
    payload
  );
  return response.data?.data;
};

export const deleteOssOrder = async (groupCode, projectCode, orderId) => {
  const response = await axiosPrivate.delete(
    `${getBasePath(groupCode, projectCode)}/orders/${encodeURIComponent(orderId)}`
  );
  return response.data?.data;
};

export const fetchOssOrderDetail = async (groupCode, projectCode, orderId) => {
  const response = await axiosPrivate.get(
    `${getBasePath(groupCode, projectCode)}/orders/${encodeURIComponent(orderId)}`
  );
  return response.data?.data;
};

export const fetchOssActions = async (groupCode, projectCode, params = {}) => {
  const response = await axiosPrivate.get(
    `${getBasePath(groupCode, projectCode)}/actions`,
    { params }
  );
  return response.data?.data;
};

export const processOssAction = async (groupCode, projectCode, orderLineId, payload) => {
  const response = await axiosPrivate.patch(
    `${getBasePath(groupCode, projectCode)}/actions/${encodeURIComponent(orderLineId)}`,
    payload
  );
  return response.data?.data;
};

export const fetchOssCalendar = async (groupCode, projectCode, params = {}) => {
  const response = await axiosPrivate.get(
    `${getBasePath(groupCode, projectCode)}/calendar`,
    { params }
  );
  return response.data?.data;
};

export const fetchOssStandards = async (groupCode, projectCode) => {
  const response = await axiosPrivate.get(`${getBasePath(groupCode, projectCode)}/standards`);
  return response.data?.data;
};

export const saveOssSettings = async (groupCode, projectCode, payload) => {
  const response = await axiosPrivate.patch(
    `${getBasePath(groupCode, projectCode)}/standards/settings`,
    payload
  );
  return response.data?.data;
};

export const saveOssItemGroup = async (groupCode, projectCode, payload, itemGroupId) => {
  const path = itemGroupId
    ? `/standards/item-groups/${encodeURIComponent(itemGroupId)}`
    : "/standards/item-groups";
  const method = itemGroupId ? "patch" : "post";
  const response = await axiosPrivate[method](`${getBasePath(groupCode, projectCode)}${path}`, payload);
  return response.data?.data;
};

export const saveOssItem = async (groupCode, projectCode, payload, itemId) => {
  const path = itemId ? `/standards/items/${encodeURIComponent(itemId)}` : "/standards/items";
  const method = itemId ? "patch" : "post";
  const response = await axiosPrivate[method](`${getBasePath(groupCode, projectCode)}${path}`, payload);
  return response.data?.data;
};

export const saveOssCalendarDay = async (groupCode, projectCode, payload) => {
  const response = await axiosPrivate.post(
    `${getBasePath(groupCode, projectCode)}/standards/calendar-days`,
    payload
  );
  return response.data?.data;
};

export const recalculateOssSchedules = async (groupCode, projectCode, payload = {}) => {
  const response = await axiosPrivate.post(
    `${getBasePath(groupCode, projectCode)}/standards/recalculate`,
    payload
  );
  return response.data?.data;
};

export const fetchOssChanges = async (groupCode, projectCode, params = {}) => {
  const response = await axiosPrivate.get(`${getBasePath(groupCode, projectCode)}/changes`, { params });
  return response.data?.data;
};

export const createOssChange = async (groupCode, projectCode, payload) => {
  const response = await axiosPrivate.post(`${getBasePath(groupCode, projectCode)}/changes`, payload);
  return response.data?.data;
};

export const reviewOssChange = async (groupCode, projectCode, changeId, payload) => {
  const response = await axiosPrivate.post(
    `${getBasePath(groupCode, projectCode)}/changes/${encodeURIComponent(changeId)}/review`,
    payload
  );
  return response.data?.data;
};

export const fetchOssDocuments = async (groupCode, projectCode, params = {}) => {
  const response = await axiosPrivate.get(`${getBasePath(groupCode, projectCode)}/documents`, { params });
  return response.data?.data;
};

export const uploadOssDocument = async (groupCode, projectCode, file, payload = {}) => {
  const formData = new FormData();
  formData.append("file", file);
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null) formData.append(key, value);
  });
  const response = await axiosPrivate.post(
    `${getBasePath(groupCode, projectCode)}/documents`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data?.data;
};

export const deleteOssDocument = async (groupCode, projectCode, documentId) => {
  const response = await axiosPrivate.delete(
    `${getBasePath(groupCode, projectCode)}/documents/${encodeURIComponent(documentId)}`
  );
  return response.data?.data;
};

export const getOssDocumentDownloadUrl = (groupCode, projectCode, documentId) =>
  `${axiosPrivate.defaults.baseURL || ""}${getBasePath(groupCode, projectCode)}/documents/${encodeURIComponent(documentId)}/download`;

export const fetchOssReport = async (groupCode, projectCode, params = {}) => {
  const response = await axiosPrivate.get(`${getBasePath(groupCode, projectCode)}/reports`, { params });
  return response.data?.data;
};

export const getOssReportCsvUrl = (groupCode, projectCode, params = {}) => {
  const query = new URLSearchParams(params).toString();
  return `${axiosPrivate.defaults.baseURL || ""}${getBasePath(groupCode, projectCode)}/reports/export.csv${query ? `?${query}` : ""}`;
};

export const fetchOssIntegrationEvents = async (groupCode, projectCode, params = {}) => {
  const response = await axiosPrivate.get(
    `${getBasePath(groupCode, projectCode)}/integrations/events`,
    { params }
  );
  return response.data?.data;
};

export const createOssIntegrationEvent = async (groupCode, projectCode, payload) => {
  const response = await axiosPrivate.post(
    `${getBasePath(groupCode, projectCode)}/integrations/events`,
    payload
  );
  return response.data?.data;
};

export const retryOssIntegrationEvent = async (groupCode, projectCode, eventId) => {
  const response = await axiosPrivate.post(
    `${getBasePath(groupCode, projectCode)}/integrations/events/${encodeURIComponent(eventId)}/retry`
  );
  return response.data?.data;
};

export const resolveOssIntegrationEvent = async (groupCode, projectCode, eventId, payload = {}) => {
  const response = await axiosPrivate.post(
    `${getBasePath(groupCode, projectCode)}/integrations/events/${encodeURIComponent(eventId)}/resolve`,
    payload
  );
  return response.data?.data;
};
